/**
 * ContactFormModal — formulário compartilhado de criar/editar Contato.
 * Único lugar canônico de edição de contatos no CRM.
 */
import { useState, useEffect, useRef } from 'react'
import { X, Trash2 } from 'lucide-react'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import { ConfirmDialog } from '@/components/ui/ConfirmDialog'
import { useCreateContact, useUpdateContact, useDeleteContact } from '@/hooks/useLeads'
import type { Contact, ContactCreate, ContactUpdate, ContactRole } from '@/types'
import { CONTACT_ROLE_LABELS } from '@/types'

const selectClass = 'flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm'

function getApiErrorMessage(err: unknown, fallback: string): string {
  const axiosErr = err as { response?: { data?: { detail?: string } } }
  if (axiosErr?.response?.data?.detail) return axiosErr.response.data.detail
  return fallback
}

export function ContactFormModal({
  contact,
  companyId,
  onClose,
  onSaved,
}: {
  contact: Contact | null
  companyId: string
  onClose: () => void
  onSaved: () => void
}) {
  const isEdit = !!contact
  const createMutation = useCreateContact()
  const updateMutation = useUpdateContact()
  const deleteMutation = useDeleteContact()
  const [form, setForm] = useState({
    first_name: contact?.first_name || '',
    last_name: contact?.last_name || '',
    email: contact?.email || '',
    phone: contact?.phone || '',
    mobile: contact?.mobile || '',
    whatsapp: contact?.whatsapp || '',
    linkedin_url: contact?.linkedin_url || '',
    instagram: contact?.instagram || '',
    twitter: contact?.twitter || '',
    job_title: contact?.job_title || '',
    department: contact?.department || '',
    role: (contact?.role || '') as ContactRole | '',
    notes: contact?.notes || '',
  })
  const [error, setError] = useState('')

  const dirtyRef = useRef(false)
  const initialMountRef = useRef(true)
  useEffect(() => {
    if (initialMountRef.current) { initialMountRef.current = false; return }
    dirtyRef.current = true
  }, [form])

  const [showDiscardConfirm, setShowDiscardConfirm] = useState(false)
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)
  const requestClose = () => {
    if (dirtyRef.current) { setShowDiscardConfirm(true); return }
    onClose()
  }

  const handleDelete = async () => {
    if (!contact) return
    setError('')
    try {
      await deleteMutation.mutateAsync(contact.id)
      setShowDeleteConfirm(false)
      onSaved()
    } catch (err: unknown) {
      setError(getApiErrorMessage(err, 'Erro ao excluir contato'))
      setShowDeleteConfirm(false)
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!form.first_name.trim()) { setError('Nome é obrigatório'); return }
    setError('')
    try {
      if (isEdit) {
        const updateData: ContactUpdate = {
          first_name: form.first_name, last_name: form.last_name || null,
          email: form.email || null, phone: form.phone || null, mobile: form.mobile || null,
          whatsapp: form.whatsapp || null, linkedin_url: form.linkedin_url || null,
          instagram: form.instagram || null, twitter: form.twitter || null,
          job_title: form.job_title || null, department: form.department || null,
          role: (form.role as ContactRole) || null, notes: form.notes || null,
        }
        await updateMutation.mutateAsync({ contactId: contact!.id, data: updateData })
      } else {
        const createData: ContactCreate = {
          company_id: companyId, first_name: form.first_name, last_name: form.last_name || null,
          email: form.email || null, phone: form.phone || null, mobile: form.mobile || null,
          whatsapp: form.whatsapp || null, linkedin_url: form.linkedin_url || null,
          instagram: form.instagram || null, twitter: form.twitter || null,
          job_title: form.job_title || null, department: form.department || null,
          role: (form.role as ContactRole) || null, notes: form.notes || null,
        }
        await createMutation.mutateAsync(createData)
      }
      onSaved()
    } catch (err: unknown) {
      setError(getApiErrorMessage(err, 'Erro ao salvar contato'))
    }
  }

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50" onClick={requestClose}>
      <div className="bg-card border rounded-lg shadow-lg w-full max-w-lg mx-4 max-h-[85vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between p-4 border-b">
          <h2 className="text-lg font-semibold">{isEdit ? 'Editar Contato' : 'Novo Contato'}</h2>
          <Button variant="ghost" size="icon" onClick={requestClose}><X className="h-4 w-4" /></Button>
        </div>
        <form onSubmit={handleSubmit} className="p-4 space-y-3">
          {error && <div className="p-2 rounded bg-destructive/10 text-destructive text-sm">{error}</div>}
          <div className="grid grid-cols-2 gap-3">
            <div><label className="text-sm font-medium">Nome *</label><Input value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })} required /></div>
            <div><label className="text-sm font-medium">Sobrenome</label><Input value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value })} /></div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><label className="text-sm font-medium">Cargo</label><Input value={form.job_title} onChange={(e) => setForm({ ...form, job_title: e.target.value })} /></div>
            <div><label className="text-sm font-medium">Departamento</label><Input value={form.department} onChange={(e) => setForm({ ...form, department: e.target.value })} /></div>
          </div>
          <div>
            <label className="text-sm font-medium">Papel na Empresa</label>
            <select className={selectClass} value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value as ContactRole | '' })}>
              <option value="">Nenhum</option>
              {Object.entries(CONTACT_ROLE_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
            </select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><label className="text-sm font-medium">Email</label><Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></div>
            <div><label className="text-sm font-medium">Telefone</label><Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} /></div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><label className="text-sm font-medium">Celular</label><Input value={form.mobile} onChange={(e) => setForm({ ...form, mobile: e.target.value })} /></div>
            <div><label className="text-sm font-medium">WhatsApp</label><Input value={form.whatsapp} onChange={(e) => setForm({ ...form, whatsapp: e.target.value })} /></div>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div><label className="text-sm font-medium">LinkedIn</label><Input placeholder="URL completa" value={form.linkedin_url} onChange={(e) => setForm({ ...form, linkedin_url: e.target.value })} /></div>
            <div><label className="text-sm font-medium">Instagram</label><Input placeholder="username" value={form.instagram} onChange={(e) => setForm({ ...form, instagram: e.target.value })} /></div>
            <div><label className="text-sm font-medium">Twitter/X</label><Input placeholder="username" value={form.twitter} onChange={(e) => setForm({ ...form, twitter: e.target.value })} /></div>
          </div>
          <div>
            <label className="text-sm font-medium">Observações</label>
            <textarea className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm min-h-[50px]" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
          </div>
          <div className="flex justify-between gap-2 pt-2">
            {isEdit ? (
              <Button
                type="button"
                variant="outline"
                onClick={() => setShowDeleteConfirm(true)}
                className="text-destructive hover:bg-destructive/10 hover:text-destructive"
                isLoading={deleteMutation.isPending}
              >
                <Trash2 className="h-4 w-4 mr-1" /> Excluir
              </Button>
            ) : <span />}
            <div className="flex gap-2">
              <Button type="button" variant="outline" onClick={requestClose}>Cancelar</Button>
              <Button type="submit" isLoading={createMutation.isPending || updateMutation.isPending}>{isEdit ? 'Salvar' : 'Adicionar Contato'}</Button>
            </div>
          </div>
        </form>
      </div>

      <ConfirmDialog
        open={showDiscardConfirm}
        title="Descartar alterações?"
        message="Os dados preenchidos no formulário do contato não serão salvos. Tem certeza que deseja sair?"
        confirmLabel="Descartar"
        cancelLabel="Continuar editando"
        variant="warning"
        onConfirm={() => { setShowDiscardConfirm(false); onClose() }}
        onCancel={() => setShowDiscardConfirm(false)}
      />

      <ConfirmDialog
        open={showDeleteConfirm}
        title="Excluir contato?"
        message={`Tem certeza que deseja excluir ${contact?.first_name || 'este contato'}? Esta ação pode ser revertida apenas via banco de dados.`}
        confirmLabel="Excluir"
        cancelLabel="Cancelar"
        variant="destructive"
        onConfirm={handleDelete}
        isLoading={deleteMutation.isPending}
        onCancel={() => setShowDeleteConfirm(false)}
      />
    </div>
  )
}
